import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { Loader } from "rsuite";
import sortUpDownIcon from "../../assets/img/sort-updown.svg";
import sortAscIcon from "../../assets/img/sort-asc.svg";
import sortDescIcon from "../../assets/img/sort-desc.svg";
import PropertyConfigPagination from "./PropertyConfigPagination";
import { getPaginatedData, getTotalPages, getNestedValue } from "./tableUtils";
import { SORT_DIRECTIONS, DEFAULT_PAGE_SIZE } from "./tableConstants";
import "./propertyConfigTable.scss";

const PropertyConfigTable = ({
  columns,
  data,
  rowKey,
  loading = false,
  loadingMessage,
  emptyMessage = "No data found.",
  className,
  pagination = "none",
  currentPage: controlledPage,
  totalPages: controlledTotalPages,
  onPageChange: controlledOnPageChange,
  pageSize = DEFAULT_PAGE_SIZE,
}) => {
  const [internalPage, setInternalPage] = useState(1);

  useEffect(() => {
    if (pagination === "client") {
      setInternalPage(1);
    }
  }, [data.length, pagination]);

  const getRowKey = (row, index) => {
    if (typeof rowKey === "function") return rowKey(row, index);
    return row[rowKey];
  };

  const renderSortIcon = (column) => {
    const isActive =
      column.sortDirection !== null && column.sortDirection !== undefined;
    let iconSrc;
    if (isActive) {
      iconSrc =
        column.sortDirection === SORT_DIRECTIONS.ASC
          ? sortAscIcon
          : sortDescIcon;
    } else {
      iconSrc = sortUpDownIcon;
    }

    return (
      <button
        type="button"
        className={`property-config-table__sort-btn${
          isActive ? " property-config-table__sort-btn--active" : ""
        }`}
        onClick={column.onSort}
        aria-label={isActive ? `Sorted ${column.sortDirection}` : "Sort"}
      >
        <img src={iconSrc} alt="" width="20" height="20" />
      </button>
    );
  };

  const renderCellContent = (column, row, rowIndex) => {
    if (column.render) return column.render(row, rowIndex);
    const value = column.accessor
      ? getNestedValue(row, column.accessor)
      : row[column.key];
    if (value === null || value === undefined || value === "") return "";
    return value;
  };

  let displayData = data;
  let activePage = 1;
  let activeTotalPages = 0;
  let activeOnPageChange = null;

  if (pagination === "client") {
    activeTotalPages = getTotalPages(data.length, pageSize);
    activePage = internalPage;
    activeOnPageChange = setInternalPage;
    displayData = getPaginatedData(data, internalPage, pageSize);
  } else if (pagination === "server") {
    activePage = controlledPage;
    activeTotalPages = controlledTotalPages;
    activeOnPageChange = controlledOnPageChange;
  }

  const rootClassName = ["property-config-table", className]
    .filter(Boolean)
    .join(" ");

  if (loading) {
    return (
      <div className={rootClassName}>
        <div className="property-config-table__loader">
          <Loader size="md" content={loadingMessage} />
        </div>
      </div>
    );
  }

  return (
    <div className={rootClassName}>
      <div className="property-config-table__scroll-area">
        <table>
          <thead>
            <tr>
              {columns.map((col) => (
                <th
                  key={col.key + col.label}
                  className={[
                    "property-config-table__th",
                    col.className,
                    col.headerClassName,
                  ]
                    .filter(Boolean)
                    .join(" ")}
                >
                  <span>{col.label}</span>
                  {col.sortable && renderSortIcon(col)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {displayData.length === 0 && (
              <tr>
                <td
                  colSpan={columns.length}
                  className="property-config-table__td property-config-table__td--empty"
                >
                  {emptyMessage}
                </td>
              </tr>
            )}
            {displayData.map((row, rowIndex) => (
              <tr
                key={getRowKey(row, rowIndex)}
                className={
                  rowIndex === displayData.length - 1
                    ? "property-config-table__row property-config-table__row--last"
                    : "property-config-table__row"
                }
              >
                {columns.map((col) => {
                  const cellContent = renderCellContent(col, row, rowIndex);
                  const cellTitle =
                    !col.render && cellContent
                      ? String(cellContent)
                      : undefined;
                  return (
                    <td
                      key={col.key + col.label}
                      title={cellTitle}
                      className={[
                        "property-config-table__td",
                        col.className,
                        col.cellClassName,
                      ]
                        .filter(Boolean)
                        .join(" ")}
                    >
                      {cellContent}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {pagination !== "none" &&
        displayData.length > 0 &&
        activeTotalPages > 1 && (
          <PropertyConfigPagination
            currentPage={activePage}
            totalPages={activeTotalPages}
            onPageChange={activeOnPageChange}
          />
        )}
    </div>
  );
};

PropertyConfigTable.propTypes = {
  columns: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      className: PropTypes.string,
      headerClassName: PropTypes.string,
      cellClassName: PropTypes.string,
      sortable: PropTypes.bool,
      sortDirection: PropTypes.oneOf(["asc", "desc", null]),
      onSort: PropTypes.func,
      render: PropTypes.func,
      accessor: PropTypes.string,
    })
  ).isRequired,
  data: PropTypes.array.isRequired,
  rowKey: PropTypes.oneOfType([PropTypes.string, PropTypes.func]).isRequired,
  loading: PropTypes.bool,
  loadingMessage: PropTypes.string,
  emptyMessage: PropTypes.string,
  className: PropTypes.string,
  pagination: PropTypes.oneOf(["none", "server", "client"]),
  currentPage: PropTypes.number,
  totalPages: PropTypes.number,
  onPageChange: PropTypes.func,
  pageSize: PropTypes.number,
};

export default PropertyConfigTable;
